/**
 * Admin → Settings.
 *
 * Single global config document (db.app_settings.key="global"). Flags here
 * affect every user. v1 toggle: show_reachable_partners controls whether
 * program cards render the "reachable partners" inline preview row.
 *
 * Layout is grouped by Display / (future: Behavior / Integrations) so new
 * flags slot in without restructuring.
 */

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { AlertCircle, CheckCircle2 } from "lucide-react";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");
  const [savedAt, setSavedAt]   = useState(null);

  async function load() {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "Admin access required." : (e?.message || "load failed"));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function patch(key, value) {
    setErr(""); setSavedAt(null);
    try {
      const { data } = await api.patch("/admin/settings", { [key]: value });
      setSettings(data);
      setSavedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "save failed");
    }
  }

  return (
    <AdminLayout activeTab="settings" title="Settings">
      <div data-testid="admin-settings-page">
        <div className="section-eyebrow">Admin · Global settings</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">App settings</h1>
        <p className="text-[13.5px] text-neutral-600 mt-2 max-w-[720px]">
          Global toggles that affect every user. Changes apply immediately
          on the next page load.
        </p>

        {err && (
          <div className="mt-4 text-[12.5px] text-red-600 inline-flex items-center gap-1.5">
            <AlertCircle size={12} /> {err}
          </div>
        )}
        {savedAt && (
          <div className="mt-4 text-[12.5px] text-green-700 inline-flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Saved at {savedAt.toLocaleTimeString()}
          </div>
        )}

        {/* DISPLAY */}
        <section className="card p-6 mt-6">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4">Display</h2>
          <Toggle
            label="Show reachable partners on program cards"
            help={
              <>
                When on, each user's dashboard program cards include a
                preview row listing airlines reachable by alliance (for
                airline programs) or by transfer (for issuer programs).
                When off, the row is hidden entirely.
              </>
            }
            disabled={loading || !settings}
            checked={!!settings?.show_reachable_partners}
            onChange={(v) => patch("show_reachable_partners", v)}
            testId="toggle-show-reachable-partners"
          />
        </section>

        {/* Future sections (Behavior, Integrations, Notifications…) slot in here. */}
      </div>
    </AdminLayout>
  );
}

function Toggle({ label, help, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-6 py-2">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13.5px]">{label}</div>
        {help && <div className="text-[12px] text-neutral-600 mt-1 max-w-[560px]">{help}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-10 shrink-0 cursor-pointer rounded-full transition-colors ${
          checked ? "bg-black" : "bg-neutral-300"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        data-testid={testId}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
